import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { applyEmptyStateAction, applyEmptyStateJsonQuery } from 'shared/actions/emptyState';
import { EmptyStateBanner } from 'shared/components/EmptyStateBanner';
import { EMPTY_STATE_TELEMETRY } from 'shared/constants/emptyStateTelemetry';
import { selectEmptyState, selectQueryString } from 'shared/selectors/results';
import { selectIsSerpFetching } from 'shared/selectors/selectIsSerpFetching';
import { setPendingEmptyStateTracking, trackChangeFiltersClick } from 'shared/tracking/emptyState';
import { EEmptyStateActionType, IEmptyStateAction, IEmptyStateTag } from 'shared/types/emptyState';
import { TThunkDispatch } from 'shared/types/redux';
import { useApplicationContext } from 'shared/utils/applicationContext';

export const EmptyStateBannerContainer: React.FC = () => {
  const { telemetry } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const emptyState = useSelector(selectEmptyState);
  const queryString = useSelector(selectQueryString);
  const isFetching = useSelector(selectIsSerpFetching);

  const handleShow = React.useCallback(() => {
    telemetry.increment(EMPTY_STATE_TELEMETRY.widgetShown);
  }, [telemetry]);

  /**
   * Аналитику откладываем: лейблом события должны быть снятые параметры запроса, а новая строка
   * запроса приезжает только вместе с обновлённой выдачей. Само событие уйдёт после `filters:updated`.
   */
  const handleTagRemove = React.useCallback(
    (tag: IEmptyStateTag) => {
      telemetry.increment(EMPTY_STATE_TELEMETRY.tagClick);

      setPendingEmptyStateTracking({ action: 'clearTag', queryString, count: tag.count });

      dispatch(applyEmptyStateJsonQuery(tag.jsonQuery));
    },
    [dispatch, queryString, telemetry],
  );

  const handleActionClick = React.useCallback(
    (action: IEmptyStateAction) => {
      if (action.type === EEmptyStateActionType.ResetAllTags) {
        setPendingEmptyStateTracking({ action: 'clearAllTags', queryString });
      }

      if (action.type === EEmptyStateActionType.ChangeFilters) {
        trackChangeFiltersClick();
      }

      dispatch(applyEmptyStateAction(action));
    },
    [dispatch, queryString],
  );

  if (!emptyState) {
    return null;
  }

  return (
    <EmptyStateBanner
      emptyState={emptyState}
      isLoading={isFetching}
      onShow={handleShow}
      onTagRemove={handleTagRemove}
      onActionClick={handleActionClick}
    />
  );
};
